import React, { useState } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import SourceUploader from './SourceUploader';
import { useContextEngine } from '../../contexts/ContextEngineContext';
import { useProjects } from '../../contexts/ProjectContext';
import { fileToBase64 } from '../../utils/fileUtils';
import './AddSourceModal.css';

interface PendingFile {
  file: File;
  name: string;
  type: string;
  size: number;
}

interface AddSourceModalProps {
  show: boolean;
  onHide: () => void;
  remainingSlots: number;
  maxFileSize: number;
  currentCount: number;
  limit: number | null;
}

const AddSourceModal: React.FC<AddSourceModalProps> = ({
  show,
  onHide,
  remainingSlots,
  maxFileSize,
  currentCount,
  limit,
}) => {
  const { currentProject } = useProjects();
  const { uploadSource } = useContextEngine();

  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleHide = () => {
    if (uploading) return;
    setPendingFiles([]);
    setErrorMessage(null);
    onHide();
  };

  const handleUpload = async () => {
    if (!currentProject?.id || pendingFiles.length === 0) return;
    setUploading(true);
    setErrorMessage(null);

    try {
      for (const pf of pendingFiles) {
        const base64 = await fileToBase64(pf.file);
        await uploadSource(currentProject.id, pf.name, pf.type, pf.size, base64);
      }
      setPendingFiles([]);
      onHide();
    } catch (err: any) {
      const responseData = err?.response?.data;
      if (responseData?.error === 'SOURCE_LIMIT_REACHED') {
        setErrorMessage(
          responseData.message ||
            "You've reached your source limit for this plan. Upgrade for more space."
        );
      } else {
        setErrorMessage(
          responseData?.error || responseData?.message || err?.message || 'Failed to upload file.'
        );
      }
    } finally {
      setUploading(false);
    }
  };

  const isUnlimited = limit === null;
  const atLimit = !isUnlimited && remainingSlots <= 0;
  const effectiveMaxFiles = isUnlimited ? 10 : remainingSlots;

  return (
    <Modal
      show={show}
      onHide={handleHide}
      centered
      backdrop={uploading ? 'static' : true}
      className="add-source-modal"
    >
      <Modal.Header closeButton={!uploading}>
        <Modal.Title>Add Source</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          {isUnlimited
            ? `${currentCount} sources in this project.`
            : `${currentCount} of ${limit} sources used for this project.`}
        </p>

        {atLimit ? (
          <Alert variant="warning" className="mb-0">
            You've reached your source limit for this plan. Delete a source or upgrade your plan to
            add more.
          </Alert>
        ) : (
          <SourceUploader
            onFilesSelected={setPendingFiles}
            maxFiles={effectiveMaxFiles}
            maxFileSize={maxFileSize}
            disabled={uploading}
            limitReachedMessage={
              isUnlimited
                ? `Maximum ${effectiveMaxFiles} files per upload batch.`
                : `Only ${remainingSlots} more source${
                    remainingSlots === 1 ? '' : 's'
                  } allowed on your plan.`
            }
          />
        )}

        {errorMessage && (
          <Alert variant="danger" className="mt-3 mb-0">
            {errorMessage}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={uploading || atLimit || pendingFiles.length === 0}
        >
          {uploading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Uploading...
            </>
          ) : (
            `Upload${pendingFiles.length > 0 ? ` (${pendingFiles.length})` : ''}`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddSourceModal;
